/**
 * cart-reward-progress.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Two-milestone Cart Reward Progress Bar for myplants.ae
 *
 * Drawer re-render (Tinker theme — confirmed from source):
 * ─────────────────────────────────────────────────────────
 * cart-items-component listens for 'cart:update' on document.
 * Its #handleCartUpdate does:
 *
 *   const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
 *   if (cartItemsHtml) {
 *     morphSection(this.sectionId, cartItemsHtml);   // ← instant, no reload
 *   } else {
 *     sectionRenderer.renderSection(this.sectionId); // ← fallback fetch
 *   }
 *
 * So we must dispatch a CustomEvent on document with:
 *   event.type  = 'cart:update'
 *   event.detail.data.sections = { [sectionId]: '<html string>' }
 *
 * We get that HTML by passing `sections: sectionId` in the /cart/add.js body
 * (Shopify returns it in response.sections). For /cart/change.js we fetch it
 * separately via /?sections= since that endpoint doesn't return sections HTML.
 *
 * IMPORTANT: event.target must NOT be the cart-items-component element itself
 * (the component skips events it dispatched). We dispatch from document so
 * event.target is always document — safe.
 * ─────────────────────────────────────────────────────────────────────────────
 */

(() => {
  'use strict';

  /* ─── Selectors / Constants ─────────────────────────────────────────────── */

  const SELECTOR    = '[data-cart-reward-progress]';
  const FILL_ATTR   = 'data-reward-fill';
  const MSG_ATTR    = 'data-reward-message';
  const CFG_ATTR    = 'data-reward-config';
  const DEBOUNCE_MS = 300;

  // Confirmed from events.js: ThemeEvents.cartUpdate = 'cart:update'
  // CartAddEvent.eventName = ThemeEvents.cartUpdate = 'cart:update'
  const CART_UPDATE_EVENT = 'cart:update';

  /* ─── State ─────────────────────────────────────────────────────────────── */

  let _lastSubtotal  = null;
  let _debounceTimer = null;
  let _fetchPatched  = false;
  let _plantBusy     = false;

  /* ─── Money formatter ───────────────────────────────────────────────────── */

  function formatMoney(subunits, symbol = 'AED') {
    const major = subunits / 100;
    const formatted = major % 1 === 0
      ? major.toLocaleString('en-AE')
      : major.toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${symbol} ${formatted}`;
  }

  function clamp(v, min, max) { return Math.min(Math.max(v, min), max); }

  /* ─── SVG helpers ───────────────────────────────────────────────────────── */

  const SVG = {
    check: `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 6l2.5 2.5 4.5-4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,

    checkMsg: `<svg class="crp-icon crp-icon--check" width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="8" fill="currentColor" opacity=".15"/>
      <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,

    truck: `<svg class="crp-icon crp-icon--truck" width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 3h13v13H1zM14 8h4l3 3v5h-7V8z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
      <circle cx="5.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="1.5"/>
      <circle cx="18.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="1.5"/>
    </svg>`,

    truckDot: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M1 3h13v13H1zM14 8h4l3 3v5h-7V8z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
      <circle cx="5.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="2"/>
      <circle cx="18.5" cy="18.5" r="2.5" stroke="currentColor" stroke-width="2"/>
    </svg>`,

    leaf: `<svg class="crp-icon crp-icon--leaf" width="15" height="17" viewBox="0 0 44 50" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
  <path d="M12.0048 12.3926C11.6188 12.3926 11.2139 12.38 10.7899 12.3504C4.71578 11.9392 0.38585 6.74873 0.204469 6.52727C-0.0760384 6.1856 -0.0675794 5.68996 0.227692 5.35883C0.432272 5.12894 5.3148 -0.255535 12.7324 0.771587C15.1094 1.1006 16.8725 2.07078 17.9735 3.65049C19.7472 6.19614 19.0786 9.36398 19.0491 9.49685C19.028 9.59597 18.9901 9.68879 18.9373 9.77526C18.8677 9.88915 17.2627 12.3905 12.009 12.3905L12.0048 12.3926ZM2.14903 5.99156C3.42503 7.29708 6.77004 10.2772 10.9102 10.5577C15.2654 10.8488 16.9358 9.35766 17.3197 8.93163C17.4104 8.31578 17.6044 6.26576 16.4866 4.66919C15.683 3.52396 14.3353 2.81108 12.4814 2.55377C7.38799 1.84301 3.55157 4.72822 2.14903 5.99156Z" fill="currentColor"/>
  <path d="M31.3914 11.8506C26.1356 11.8506 24.5326 9.34925 24.463 9.23536C24.385 9.10671 24.3386 8.96118 24.3302 8.81144C24.326 8.73551 24.0181 1.204 31.771 0.12837C39.273 -0.909297 43.0862 4.67765 43.2465 4.91597C43.4659 5.24499 43.4448 5.67947 43.1917 5.98529C43.0103 6.20674 38.6804 11.3951 32.6062 11.8085C32.1802 11.838 31.7752 11.8506 31.3893 11.8506H31.3914ZM26.125 8.44446C26.5764 8.91478 28.2742 10.2941 32.486 10.0136C36.7295 9.72679 40.1166 6.62644 41.3272 5.36732C40.2495 4.11031 37.145 1.19344 32.0199 1.90631C26.6059 2.65714 26.1503 7.24861 26.125 8.44235V8.44446Z" fill="currentColor"/>
  <path d="M21.5842 23.866C21.5652 23.866 21.5441 23.866 21.5251 23.866C21.0295 23.8344 20.6541 23.4063 20.6857 22.9106C21.5758 9.20372 16.6194 8.1323 13.0002 7.34772C12.5152 7.24227 12.2072 6.76349 12.3127 6.2784C12.4181 5.79332 12.8969 5.48752 13.382 5.59087C18.488 6.69603 23.3938 8.95272 22.4806 23.0266C22.4489 23.5012 22.0545 23.866 21.5842 23.866Z" fill="currentColor"/>
  <path d="M21.3354 15.9464C21.2869 15.9464 21.2384 15.9422 21.1898 15.9338C20.7005 15.8536 20.3673 15.3917 20.4474 14.9024C21.2341 10.0789 24.7268 6.77193 30.2842 5.58873C30.7693 5.48328 31.248 5.7954 31.3514 6.28049C31.4547 6.76558 31.1447 7.24435 30.6596 7.3477C27.2767 8.069 23.0691 10.0136 22.2233 15.1935C22.1516 15.6343 21.7698 15.9464 21.3375 15.9464H21.3354Z" fill="currentColor"/>
  <path d="M21.54 27.715C13.8904 27.715 5.50466 26.6626 3.96081 24.3089C3.90809 24.2309 3.86589 24.1465 3.83847 24.0579L3.70351 23.6129C3.6782 23.5285 3.66345 23.44 3.66345 23.3514C3.66345 19.0383 20.8103 18.9877 21.54 18.9877C22.2698 18.9877 39.4166 19.0383 39.4166 23.3514C39.4166 23.44 39.4039 23.5285 39.3765 23.6129L39.2415 24.0579C39.212 24.1549 39.1656 24.2477 39.1044 24.3279C37.5226 26.6647 29.1643 27.7129 21.5379 27.7129L21.54 27.715ZM5.51518 23.3914C6.40943 24.4734 12.3233 25.916 21.5421 25.916C30.7609 25.916 36.6748 24.4734 37.569 23.3914L37.588 23.3261C36.9384 22.377 31.265 20.7867 21.5421 20.7867C11.8193 20.7867 6.14583 22.377 5.49623 23.3261L5.51518 23.3914Z" fill="currentColor"/>
  <path d="M4.69896 24.6949C4.39103 24.6949 4.09156 24.5346 3.92705 24.2562C3.75411 23.963 3.66553 23.6593 3.66553 23.3514C3.66553 22.9064 3.99241 22.5289 4.4311 22.4614C4.86979 22.3981 5.2937 22.6617 5.42236 23.0878L5.55737 23.5328C5.69024 23.9672 5.4772 24.4333 5.06171 24.6168C4.9436 24.6696 4.82128 24.6928 4.69896 24.6928V24.6949ZM5.38652 23.3514H5.46036C5.46036 23.324 5.45402 23.3071 5.45402 23.3071L5.38652 23.3514Z" fill="currentColor"/>
  <path d="M38.383 24.6949C38.2607 24.6949 38.1384 24.6696 38.0202 24.6189C37.6048 24.4355 37.3917 23.9693 37.5246 23.5349L37.6596 23.0899C37.7904 22.6638 38.2143 22.3981 38.6509 22.4635C39.0917 22.5288 39.4165 22.9085 39.4165 23.3535C39.4165 23.6614 39.3279 23.9651 39.1549 24.2583C38.9883 24.5388 38.6909 24.697 38.383 24.697V24.6949Z" fill="currentColor"/>
  <path d="M21.54 49.755C20.439 49.755 17.0582 49.7339 16.0522 49.7001C12.0048 49.5441 8.49317 46.8592 7.30787 43.0165C7.29732 42.9827 7.28892 42.949 7.28259 42.9131L3.8195 23.9631C3.74146 23.5391 3.97554 23.1215 4.37627 22.9634C4.777 22.8073 5.23254 22.957 5.46243 23.3198C6.14999 24.4081 12.1208 25.9161 21.54 25.9161C30.9591 25.9161 36.9299 24.4081 37.6175 23.3198C37.8474 22.957 38.3029 22.8073 38.7037 22.9634C39.1044 23.1194 39.3385 23.5391 39.2604 23.9631L35.7973 42.9131C35.791 42.9469 35.7826 42.9827 35.7721 43.0165C35.1921 44.9041 34.0489 46.5407 32.4713 47.7492C30.8958 48.9493 29.0166 49.6242 27.032 49.7001C26.0196 49.7339 22.6388 49.755 21.5379 49.755H21.54ZM9.04158 42.5377C10.0181 45.6254 12.8526 47.7788 16.1175 47.9053C17.0624 47.937 20.4116 47.9581 21.5421 47.9581C22.6726 47.9581 26.0218 47.9349 26.9709 47.9053C28.578 47.8421 30.1049 47.2958 31.383 46.3214C32.6464 45.3555 33.5659 44.0457 34.0405 42.5398L37.1176 25.7073C33.8296 27.0845 27.4644 27.7151 21.5421 27.7151C15.6198 27.7151 9.25458 27.0845 5.96652 25.7073L9.04369 42.5398L9.04158 42.5377Z" fill="currentColor"/>
</svg>`,

leafDot: `<svg width="14" height="16" viewBox="0 0 44 50" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
  <path d="M12.0048 12.3926C11.6188 12.3926 11.2139 12.38 10.7899 12.3504C4.71578 11.9392 0.38585 6.74873 0.204469 6.52727C-0.0760384 6.1856 -0.0675794 5.68996 0.227692 5.35883C0.432272 5.12894 5.3148 -0.255535 12.7324 0.771587C15.1094 1.1006 16.8725 2.07078 17.9735 3.65049C19.7472 6.19614 19.0786 9.36398 19.0491 9.49685C19.028 9.59597 18.9901 9.68879 18.9373 9.77526C18.8677 9.88915 17.2627 12.3905 12.009 12.3905L12.0048 12.3926ZM2.14903 5.99156C3.42503 7.29708 6.77004 10.2772 10.9102 10.5577C15.2654 10.8488 16.9358 9.35766 17.3197 8.93163C17.4104 8.31578 17.6044 6.26576 16.4866 4.66919C15.683 3.52396 14.3353 2.81108 12.4814 2.55377C7.38799 1.84301 3.55157 4.72822 2.14903 5.99156Z" fill="currentColor"/>
  <path d="M31.3914 11.8506C26.1356 11.8506 24.5326 9.34925 24.463 9.23536C24.385 9.10671 24.3386 8.96118 24.3302 8.81144C24.326 8.73551 24.0181 1.204 31.771 0.12837C39.273 -0.909297 43.0862 4.67765 43.2465 4.91597C43.4659 5.24499 43.4448 5.67947 43.1917 5.98529C43.0103 6.20674 38.6804 11.3951 32.6062 11.8085C32.1802 11.838 31.7752 11.8506 31.3893 11.8506H31.3914ZM26.125 8.44446C26.5764 8.91478 28.2742 10.2941 32.486 10.0136C36.7295 9.72679 40.1166 6.62644 41.3272 5.36732C40.2495 4.11031 37.145 1.19344 32.0199 1.90631C26.6059 2.65714 26.1503 7.24861 26.125 8.44235V8.44446Z" fill="currentColor"/>
  <path d="M21.5842 23.866C21.5652 23.866 21.5441 23.866 21.5251 23.866C21.0295 23.8344 20.6541 23.4063 20.6857 22.9106C21.5758 9.20372 16.6194 8.1323 13.0002 7.34772C12.5152 7.24227 12.2072 6.76349 12.3127 6.2784C12.4181 5.79332 12.8969 5.48752 13.382 5.59087C18.488 6.69603 23.3938 8.95272 22.4806 23.0266C22.4489 23.5012 22.0545 23.866 21.5842 23.866Z" fill="currentColor"/>
  <path d="M21.3354 15.9464C21.2869 15.9464 21.2384 15.9422 21.1898 15.9338C20.7005 15.8536 20.3673 15.3917 20.4474 14.9024C21.2341 10.0789 24.7268 6.77193 30.2842 5.58873C30.7693 5.48328 31.248 5.7954 31.3514 6.28049C31.4547 6.76558 31.1447 7.24435 30.6596 7.3477C27.2767 8.069 23.0691 10.0136 22.2233 15.1935C22.1516 15.6343 21.7698 15.9464 21.3375 15.9464H21.3354Z" fill="currentColor"/>
  <path d="M21.54 27.715C13.8904 27.715 5.50466 26.6626 3.96081 24.3089C3.90809 24.2309 3.86589 24.1465 3.83847 24.0579L3.70351 23.6129C3.6782 23.5285 3.66345 23.44 3.66345 23.3514C3.66345 19.0383 20.8103 18.9877 21.54 18.9877C22.2698 18.9877 39.4166 19.0383 39.4166 23.3514C39.4166 23.44 39.4039 23.5285 39.3765 23.6129L39.2415 24.0579C39.212 24.1549 39.1656 24.2477 39.1044 24.3279C37.5226 26.6647 29.1643 27.7129 21.5379 27.7129L21.54 27.715ZM5.51518 23.3914C6.40943 24.4734 12.3233 25.916 21.5421 25.916C30.7609 25.916 36.6748 24.4734 37.569 23.3914L37.588 23.3261C36.9384 22.377 31.265 20.7867 21.5421 20.7867C11.8193 20.7867 6.14583 22.377 5.49623 23.3261L5.51518 23.3914Z" fill="currentColor"/>
  <path d="M4.69896 24.6949C4.39103 24.6949 4.09156 24.5346 3.92705 24.2562C3.75411 23.963 3.66553 23.6593 3.66553 23.3514C3.66553 22.9064 3.99241 22.5289 4.4311 22.4614C4.86979 22.3981 5.2937 22.6617 5.42236 23.0878L5.55737 23.5328C5.69024 23.9672 5.4772 24.4333 5.06171 24.6168C4.9436 24.6696 4.82128 24.6928 4.69896 24.6928V24.6949ZM5.38652 23.3514H5.46036C5.46036 23.324 5.45402 23.3071 5.45402 23.3071L5.38652 23.3514Z" fill="currentColor"/>
  <path d="M38.383 24.6949C38.2607 24.6949 38.1384 24.6696 38.0202 24.6189C37.6048 24.4355 37.3917 23.9693 37.5246 23.5349L37.6596 23.0899C37.7904 22.6638 38.2143 22.3981 38.6509 22.4635C39.0917 22.5288 39.4165 22.9085 39.4165 23.3535C39.4165 23.6614 39.3279 23.9651 39.1549 24.2583C38.9883 24.5388 38.6909 24.697 38.383 24.697V24.6949Z" fill="currentColor"/>
  <path d="M21.54 49.755C20.439 49.755 17.0582 49.7339 16.0522 49.7001C12.0048 49.5441 8.49317 46.8592 7.30787 43.0165C7.29732 42.9827 7.28892 42.949 7.28259 42.9131L3.8195 23.9631C3.74146 23.5391 3.97554 23.1215 4.37627 22.9634C4.777 22.8073 5.23254 22.957 5.46243 23.3198C6.14999 24.4081 12.1208 25.9161 21.54 25.9161C30.9591 25.9161 36.9299 24.4081 37.6175 23.3198C37.8474 22.957 38.3029 22.8073 38.7037 22.9634C39.1044 23.1194 39.3385 23.5391 39.2604 23.9631L35.7973 42.9131C35.791 42.9469 35.7826 42.9827 35.7721 43.0165C35.1921 44.9041 34.0489 46.5407 32.4713 47.7492C30.8958 48.9493 29.0166 49.6242 27.032 49.7001C26.0196 49.7339 22.6388 49.755 21.5379 49.755H21.54ZM9.04158 42.5377C10.0181 45.6254 12.8526 47.7788 16.1175 47.9053C17.0624 47.937 20.4116 47.9581 21.5421 47.9581C22.6726 47.9581 26.0218 47.9349 26.9709 47.9053C28.578 47.8421 30.1049 47.2958 31.383 46.3214C32.6464 45.3555 33.5659 44.0457 34.0405 42.5398L37.1176 25.7073C33.8296 27.0845 27.4644 27.7151 21.5421 27.7151C15.6198 27.7151 9.25458 27.0845 5.96652 25.7073L9.04369 42.5398L9.04158 42.5377Z" fill="currentColor"/>
</svg>`,

    star: `<svg class="crp-icon crp-icon--party" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#f5a623"/>
    </svg>`
  };

  /* ─── Config reader ─────────────────────────────────────────────────────── */

  function readConfig(root) {
    try {
      const el = root.querySelector(`[${CFG_ATTR}]`);
      return el ? JSON.parse(el.textContent) : null;
    } catch { return null; }
  }

  /* ─── Section ID helper ─────────────────────────────────────────────────── */

  /**
   * Returns ALL cart-items-component section IDs currently in the DOM.
   * cart-items-component#handleCartUpdate looks up sections[this.sectionId]
   * so we must include every instance (drawer + cart page if both are present).
   *
   * @returns {string[]}
   */
  function getCartSectionIds() {
    const ids = new Set();

    document.querySelectorAll('cart-items-component[data-section-id]').forEach(el => {
      ids.add(el.dataset.sectionId);
    });

    // Fallback: shopify-section wrapper around the cart drawer
    if (ids.size === 0) {
      const sectionEl = document.querySelector('cart-drawer-component')
        ?.closest('[id^="shopify-section-"]');
      if (sectionEl) {
        ids.add(sectionEl.id.replace('shopify-section-', ''));
      }
    }

    return [...ids];
  }

  /* ─── Cart fetch ────────────────────────────────────────────────────────── */

  async function fetchCart() {
    const res = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    return res.json();
  }

  /* ─── Sections HTML fetch ───────────────────────────────────────────────── */

  /**
   * Fetches fresh section HTML from Shopify's Sections API.
   * Used after /cart/change.js which doesn't return sections in its response.
   *
   * @param {string[]} sectionIds
   * @returns {Promise<Record<string,string>>}  e.g. { 'header': '<html>...' }
   */
  async function fetchSectionsHtml(sectionIds) {
    if (!sectionIds.length) return {};
    try {
      const res = await fetch(
        `/?sections=${encodeURIComponent(sectionIds.join(','))}`
      );
      if (!res.ok) return {};
      return await res.json();
    } catch {
      return {};
    }
  }

  /* ─── Dispatch 'cart:update' to Tinker ─────────────────────────────────── */

  /**
   * Dispatches the 'cart:update' CustomEvent that cart-items-component listens
   * for. The shape matches what CartUpdateEvent / CartAddEvent produce:
   *
   *   event.detail = {
   *     resource: <cart object>,
   *     sourceId: <string>,
   *     data: { source, sections: { [sectionId]: '<html>' } }
   *   }
   *
   * cart-items-component#handleCartUpdate then does:
   *   const html = event.detail.data.sections?.[this.sectionId];
   *   if (html) morphSection(this.sectionId, html);
   *
   * We dispatch from `document` (not from inside cart-items-component)
   * so event.target !== the component — bypassing the self-event guard.
   *
   * @param {object} cart     - Latest cart object from /cart.js
   * @param {object} sections - Sections HTML keyed by section ID
   */
  function notifyTinker(cart, sections) {
    const event = new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'cart-reward-progress',
        data: {
          source: 'cart-reward-progress',
          sections,          // { [sectionId]: '<html string>' }
        },
      },
    });

    // Dispatch on document so event.target is document, not a cart component.
    // This prevents cart-items-component from skipping it via its self-guard:
    //   if (event.target === this) return;
    document.dispatchEvent(event);
  }

  /* ─── Free Plant Auto-Add / Remove ─────────────────────────────────────── */

  /**
   * Adds or removes the free plant gift line and notifies Tinker so the
   * cart drawer morphs immediately without a page reload.
   *
   * ADD  → /cart/add.js with `sections` in body → sections come back in
   *         response.sections → dispatch cart:update with that HTML
   *
   * REMOVE → /cart/change.js (no sections in response) → fetch sections
   *           separately → dispatch cart:update with that HTML
   *
   * @param {object} cart
   * @param {object} config
   * @returns {Promise<object|null>} Post-mutation cart, or null if no change
   */
  async function syncFreePlant(cart, config) {
    const { threshold2, freePlantVariantId } = config;
    if (!freePlantVariantId) return null;

    const subtotal   = cart.items_subtotal_price;
    const shouldHave = subtotal >= threshold2;

    // Match on the _gift marker, not just the variant: a customer may also
    // have bought this same plant themselves, and that line is theirs.
    const giftLines = cart.items.filter(
      item => item.variant_id === freePlantVariantId &&
              item.properties && item.properties._gift === 'free_plant'
    );
    const existingLine = giftLines[0] || null;

    // /cart/add.js MERGES a repeat add into the existing line, so a stale
    // cart read after a mutation could push the gift to qty 2, 3... Treat
    // any quantity above 1 as a defect to repair rather than leave.
    const needsQtyFix = shouldHave && existingLine && existingLine.quantity > 1;

    if (shouldHave && existingLine && !needsQtyFix) return null; // present, qty 1 ✓
    if (!shouldHave && !existingLine) return null;               // already absent ✓

    if (_plantBusy) return null;
    _plantBusy = true;

    const sectionIds = getCartSectionIds();

    try {

      /* ── REPAIR QUANTITY ─────────────────────────────────────────────── */
      // The gift is present but at qty > 1 (or duplicated across lines).
      // Force the first line back to 1 and drop any extras.
      if (needsQtyFix || (shouldHave && giftLines.length > 1)) {
        for (let i = giftLines.length - 1; i >= 1; i--) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: giftLines[i].key, quantity: 0 }),
          });
        }
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingLine.key, quantity: 1 }),
        });
        const [repairedCart, repairSections] = await Promise.all([
          fetchCart(),
          fetchSectionsHtml(sectionIds),
        ]);
        notifyTinker(repairedCart, repairSections);
        console.log('[CartReward] Free plant quantity corrected to 1 ✓');
        return repairedCart;
      }

      /* ── ADD ─────────────────────────────────────────────────────────── */
      if (shouldHave && !existingLine) {

        const body = {
          items: [{
            id: freePlantVariantId,
            quantity: 1,
            properties: { _gift: 'free_plant' },
          }],
        };

        // Pass sections so Shopify returns fresh HTML in the same response.
        // This is the same pattern ProductFormComponent uses.
        if (sectionIds.length) {
          body.sections = sectionIds.join(',');
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          console.error('[CartReward] Failed to add free plant:', await res.text());
          return null;
        }

        const addResponse = await res.json();
        // addResponse.sections = { [sectionId]: '<html>' } when sections were passed
        const sections  = addResponse.sections || {};
        let updatedCart = await fetchCart();

        // Shopify's cart read can lag a mutation. If the gift is not visible
        // yet, re-read once rather than letting the next cycle add it again.
        const seesGift = c => (c.items || []).some(
          i => i.variant_id === freePlantVariantId &&
               i.properties && i.properties._gift === 'free_plant'
        );
        if (!seesGift(updatedCart)) {
          await new Promise(r => setTimeout(r, 300));
          updatedCart = await fetchCart();
        }

        // Notify Tinker — cart-items-component will morph the drawer
        notifyTinker(updatedCart, sections);

        console.log('[CartReward] Free plant added ✓');
        return updatedCart;
      }

      /* ── REMOVE ──────────────────────────────────────────────────────── */
      if (!shouldHave && existingLine) {

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingLine.key, quantity: 0 }),
        });

        if (!res.ok) {
          console.error('[CartReward] Failed to remove free plant:', await res.text());
          return null;
        }

        // /cart/change.js doesn't return sections — fetch separately
        const [updatedCart, sections] = await Promise.all([
          fetchCart(),
          fetchSectionsHtml(sectionIds),
        ]);

        // Notify Tinker — cart-items-component will morph the drawer
        notifyTinker(updatedCart, sections);

        console.log('[CartReward] Free plant removed ✓');
        return updatedCart;
      }

    } catch (err) {
      console.error('[CartReward] syncFreePlant error:', err);
      return null;
    } finally {
      _plantBusy = false;
    }

    return null;
  }

  /* ─── Build message HTML ────────────────────────────────────────────────── */

  function buildMessage(subtotal, t1, t2, symbol, m1, m2) {
    if (m2) {
      return `
        <div class="cart-reward-progress__congrats-wrap">
          ${SVG.star}
          <p class="cart-reward-progress__congrats" role="status" aria-live="polite">
            You've unlocked everything!
          </p>
        </div>
        <ul class="cart-reward-progress__unlocked-list" aria-label="Rewards unlocked">
          <li>${SVG.checkMsg} FREE Shipping</li>
          <li>${SVG.checkMsg} FREE Plant</li>
        </ul>`;
    }

    if (m1) {
      const rem = formatMoney(t2 - subtotal, symbol);
      return `
        <div class="cart-reward-progress__status" role="status" aria-live="polite">
          ${SVG.checkMsg} FREE Shipping unlocked!
        </div>
        <p class="cart-reward-progress__nudge">
          Add <strong>${rem}</strong> more for a <strong>FREE Plant</strong> ${SVG.leaf}
        </p>`;
    }

    const rem = formatMoney(t1 - subtotal, symbol);
    return `
      <p class="cart-reward-progress__nudge" role="status" aria-live="polite">
        Add <strong>${rem}</strong> more for <strong>FREE Shipping</strong> ${SVG.truck}
      </p>`;
  }

  /* ─── Render progress bar ───────────────────────────────────────────────── */

  function renderBar(root, cart) {
    const config = readConfig(root);
    if (!config) return;

    const { threshold1, threshold2, currencySymbol } = config;
    const subtotal = cart.items_subtotal_price;
    const m1 = subtotal >= threshold1;
    const m2 = subtotal >= threshold2;

    // Fill width
    const fillEl = root.querySelector(`[${FILL_ATTR}]`);
    if (fillEl) {
      fillEl.style.width = `${clamp((subtotal / threshold2) * 100, 0, 100)}%`;
      const track = fillEl.closest('[role="progressbar"]');
      if (track) {
        track.setAttribute('aria-valuenow', subtotal);
        track.setAttribute('aria-valuemax', threshold2);
      }
    }

    // Milestone dots
    root.querySelectorAll('[data-milestone]').forEach(dot => {
      const idx        = parseInt(dot.dataset.milestone, 10);
      const reached    = idx === 1 ? m1 : m2;
      const wasReached = dot.classList.contains('cart-reward-progress__milestone--reached');

      dot.classList.toggle('cart-reward-progress__milestone--reached', reached);

      const iconEl = dot.querySelector('.cart-reward-progress__milestone-dot');
      if (iconEl) {
        iconEl.innerHTML = reached
          ? SVG.check
          : (idx === 1 ? SVG.truckDot : SVG.leafDot);
      }

      if (reached && !wasReached) {
        dot.classList.add('cart-reward-progress__milestone--bounce');
        setTimeout(
          () => dot.classList.remove('cart-reward-progress__milestone--bounce'),
          700
        );
      }
    });

    // Message
    const msgEl = root.querySelector(`[${MSG_ATTR}]`);
    if (msgEl) {
      msgEl.innerHTML = buildMessage(
        subtotal, threshold1, threshold2, currencySymbol, m1, m2
      );
    }
  }

  /* ─── Confetti ──────────────────────────────────────────────────────────── */

  function burstConfetti(root) {
    const container = document.createElement('div');
    container.className = 'cart-reward-progress__confetti-container';
    root.appendChild(container);

    const pieces = ['🌿', '⭐', '✨', '🌱', '🎊', '🌿'];
    for (let i = 0; i < 20; i++) {
      const span = document.createElement('span');
      span.className = 'cart-reward-progress__confetti-piece';
      span.textContent = pieces[i % pieces.length];
      span.style.setProperty('--x', `${Math.random() * 100}%`);
      span.style.setProperty('--delay', `${Math.random() * 0.45}s`);
      span.style.setProperty('--duration', `${0.65 + Math.random() * 0.7}s`);
      container.appendChild(span);
    }

    setTimeout(() => container.remove(), 1800);
  }

  /* ─── Debounce ──────────────────────────────────────────────────────────── */

  function debounce(fn, delay) {
    return (...args) => {
      clearTimeout(_debounceTimer);
      _debounceTimer = setTimeout(() => fn(...args), delay);
    };
  }

  /* ─── Main update pipeline ──────────────────────────────────────────────── */

  const handleCartChange = debounce(async () => {
    let cart;
    try {
      cart = await fetchCart();
    } catch (err) {
      console.error('[CartReward] Could not fetch cart:', err);
      return;
    }

    const subtotal  = cart.items_subtotal_price;
    const firstRoot = document.querySelector(SELECTOR);
    if (!firstRoot) return;

    const config = readConfig(firstRoot);

    // Confetti — fires once on crossing threshold_2 upward
    if (
      config &&
      _lastSubtotal !== null &&
      _lastSubtotal < config.threshold2 &&
      subtotal >= config.threshold2
    ) {
      document.querySelectorAll(SELECTOR).forEach(r => burstConfetti(r));
    }

    _lastSubtotal = subtotal;

    // Sync free plant. If a mutation happened, syncFreePlant already
    // dispatched 'cart:update' with sections HTML — Tinker morphs the drawer.
    // It also returns the post-mutation cart so our progress bar stays in sync.
    if (config?.freePlantVariantId) {
      const mutatedCart = await syncFreePlant(cart, config);
      if (mutatedCart) cart = mutatedCart;
    }

    // Re-render progress bar instances
    document.querySelectorAll(SELECTOR).forEach(root => renderBar(root, cart));

  }, DEBOUNCE_MS);

  /* ─── Fetch interception ────────────────────────────────────────────────── */

  /**
   * Intercepts cart mutation fetches made by Tinker's own components
   * (quantity +/-, remove buttons in drawer) and schedules our handler.
   * We wait 150 ms so Tinker's own CartUpdateEvent has already fired and
   * morphed the section before we run — avoiding double-morph.
   */
  function patchFetch() {
    if (_fetchPatched) return;
    _fetchPatched = true;

    const _fetch = window.fetch;
    window.fetch = async function (input, init, ...rest) {
      const url    = typeof input === 'string' ? input : (input?.url ?? '');
      const isMutation = /\/cart\/(add|change|clear|update)(\.js)?(\?|$)/.test(url);

      const result = await _fetch.apply(this, [input, init, ...rest]);

      if (isMutation) {
        result.clone().json().then(() => {
          setTimeout(handleCartChange, 150);
        }).catch(() => {});
      }

      return result;
    };

    // XHR fallback (some third-party apps)
    const _open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      if (typeof url === 'string' && /\/cart\/(add|change|clear|update)/.test(url)) {
        this.addEventListener('load', () => setTimeout(handleCartChange, 150));
      }
      return _open.call(this, method, url, ...rest);
    };
  }

  /* ─── Event listeners ───────────────────────────────────────────────────── */

  function attachListeners() {
    // 'cart:update' — the single event name used by both CartAddEvent and
    // CartUpdateEvent in Tinker (confirmed from events.js source).
    // Listening here means we also react when other theme components
    // (product forms, quantity selectors) update the cart.
    document.addEventListener(CART_UPDATE_EVENT, handleCartChange);

    patchFetch();
  }

  /* ─── Bootstrap ─────────────────────────────────────────────────────────── */

  function init() {
    const roots = document.querySelectorAll(SELECTOR);
    if (!roots.length) return;

    _lastSubtotal = parseInt(roots[0].dataset.subtotal ?? '0', 10);

    attachListeners();

    // Initial run — syncs bar and free plant on page load.
    // Handles the case where subtotal is already >= threshold_2 after refresh.
    handleCartChange();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();